import type { EmptyProps as AntdEmptyProps } from 'antd/lib/empty'
import LocaleReceiver from 'antd/lib/locale-provider/LocaleReceiver'
import classNames from 'classnames'
import React, { ReactNode } from 'react'

import { ConfigConsumer } from 'antd/lib/config-provider'
import { default as AntdEmpty } from 'antd/lib/empty'
import {
  Illustration403,
  Illustration404,
  Illustration500,
  IllustrationEmpty,
  IllustrationFailure,
  IllustrationNoAccess,
  IllustrationNoChart,
  IllustrationNoResult,
  IllustrationOffline,
  IllustrationSuccess,
} from '../svg-components'

export * from 'antd/lib/empty'

export interface EmptyProps extends AntdEmptyProps {
  type?:
    | 'empty'
    | 'no-result'
    | 'no-chart'
    | 'no-permission'
    | 'failure'
    | 'success'
    | 'offline'
    | '403'
    | '404'
    | '500'
  size?: 'mini' | 'small' | 'middle' | 'large' | 'ultra'
  locale?: {
    noGraphData?: string
    noPermission?: string
    createdSuccessfully?: string
    creationFailed?: string
    noNetwork?: string
    noData?: string
  }
}

// description map for evert type
export const EmptyDescriptionMap: Record<
  NonNullable<EmptyProps['type']>,
  keyof NonNullable<EmptyProps['locale']> | undefined
> = {
  empty: 'noData',
  'no-result': 'noData',
  'no-chart': 'noGraphData',
  'no-permission': 'noPermission',
  failure: 'creationFailed',
  success: 'createdSuccessfully',
  offline: 'noNetwork',
  403: undefined,
  404: undefined,
  500: undefined,
}

const BuiltinEmptyImage: Record<NonNullable<EmptyProps['type']>, ReactNode> = {
  empty: <IllustrationEmpty />,
  'no-result': <IllustrationNoResult />,
  'no-chart': <IllustrationNoChart />,
  'no-permission': <IllustrationNoAccess />,
  failure: <IllustrationFailure />,
  success: <IllustrationSuccess />,
  offline: <IllustrationOffline />,
  '403': <Illustration403 />,
  '404': <Illustration404 />,
  '500': <Illustration500 />,
}

export function Empty({
  className,
  description,
  size = 'middle',
  type = 'empty',
  locale,
  ...rest
}: EmptyProps) {
  return (
    <ConfigConsumer>
      {({ getPrefixCls }) => {
        const prefixCls = getPrefixCls('empty')

        return (
          <LocaleReceiver componentName="Empty" defaultLocale={locale}>
            {(locale) => {
              const defaultDescription = EmptyDescriptionMap[type]
                ? (locale as Record<string, string>)[EmptyDescriptionMap[type]!]
                : undefined

              return (
                <AntdEmpty
                  className={classNames(prefixCls, className, {
                    [`${prefixCls}-mini`]: size === 'mini',
                    [`${prefixCls}-small`]: size === 'small',
                    [`${prefixCls}-large`]: size === 'large',
                    [`${prefixCls}-ultra`]: size === 'ultra',
                  })}
                  image={BuiltinEmptyImage[type]}
                  description={description || defaultDescription}
                  {...rest}
                />
              )
            }}
          </LocaleReceiver>
        )
      }}
    </ConfigConsumer>
  )
}
